import Keycloak from 'keycloak-js';

// Environment-based Keycloak configuration
export const getKeycloakConfig = () => {
  // Check for explicit environment variable first (Vite uses import.meta.env)
  const explicitEnv = import.meta.env.VITE_KEYCLOAK_ENVIRONMENT;
  
  // Detect environment based on hostname or explicit env var
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  
  let environment = 'local'; // default
  
  console.log('DEBUG: Keycloak Config Loading');
  console.log('DEBUG: VITE_KEYCLOAK_URL:', import.meta.env.VITE_KEYCLOAK_URL);
  console.log('DEBUG: VITE_KEYCLOAK_ENVIRONMENT:', import.meta.env.VITE_KEYCLOAK_ENVIRONMENT);
  console.log('DEBUG: hostname:', hostname);
  
  if (explicitEnv) {
    environment = explicitEnv;
  } else if (hostname.includes('prod.dhdp.ca')) {
    environment = 'prod';
  } else if (hostname.includes('qa.dhdp.ca')) {
    environment = 'qa';
  } else if (hostname.includes('dev.dhdp.ca')) {
    environment = 'dev';
  }

  const configs = {
    local: {
      // For local development, use localhost:8080 as default to avoid accidental dev redirect
      // IMPORTANT: No /auth in the URL for modern Keycloak
      url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8080',
      realm: import.meta.env.VITE_KEYCLOAK_REALM || 'dhdp-flower',
      clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'dhdp-flower-frontend',
      redirectUri: import.meta.env.VITE_KEYCLOAK_REDIRECT_URI || 'http://localhost:3000',
    },
    dev: {
      url: import.meta.env.VITE_KEYCLOAK_URL || 'https://keycloak.dev.dhdp.ca',
      realm: import.meta.env.VITE_KEYCLOAK_REALM || 'dhdp-flower',
      clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'dhdp-flower-frontend',
      redirectUri: import.meta.env.VITE_KEYCLOAK_REDIRECT_URI || 'https://flower.dev.dhdp.ca/auth-callback',
    },
    qa: {
      url: 'https://keycloak.qa.dhdp.ca',
      realm: 'dhdp-flower',
      clientId: 'dhdp-flower-frontend',
      redirectUri: typeof window !== 'undefined' ? window.location.origin : 'https://flower.qa.dhdp.ca',
    },
    prod: {
      url: 'https://keycloak.prod.dhdp.ca',
      realm: 'dhdp-flower',
      clientId: 'dhdp-flower-frontend',
      redirectUri: typeof window !== 'undefined' ? window.location.origin : 'https://flower.prod.dhdp.ca',
    },
  };

  const base = configs[environment] || configs.local;

  // --- Per-business Keycloak (true federation) -----------------------------
  // When federation is enabled, a tenant's Keycloak (issuer/realm/clientId) is
  // resolved from the BFF's /api/tenant-config and cached in localStorage by
  // bootstrapTenantConfig(). We read it synchronously here so the Keycloak
  // instance points at the right business realm. When the flag is off (the
  // default, and current local/dev), this block is inert and behaviour is
  // identical to before.
  if (FEDERATION_ENABLED && typeof window !== 'undefined') {
    try {
      const cached = JSON.parse(window.localStorage.getItem(TENANT_KC_KEY) || 'null');
      if (cached && cached.url && cached.realm && cached.clientId) {
        console.log('Keycloak: using per-tenant config', cached);
        return { ...base, url: cached.url, realm: cached.realm, clientId: cached.clientId };
      }
    } catch (_) { /* ignore malformed cache */ }
  }

  console.log(`Keycloak environment: ${environment}`);
  return base;
};

const FEDERATION_ENABLED = import.meta.env.VITE_FEDERATION_ENABLED === 'true';
const TENANT_KC_KEY = 'fx_tenant_kc';

// Fetch the tenant's Keycloak config from the BFF (subdomain → business realm)
// and cache it. Call this once at app startup BEFORE the Keycloak instance is
// needed. Returns true if the cached tenant changed (caller may reload so the
// new realm takes effect). No-op unless VITE_FEDERATION_ENABLED === 'true'.
export const bootstrapTenantConfig = async () => {
  if (!FEDERATION_ENABLED || typeof window === 'undefined') return false;
  try {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/api';
    const host = window.location.host;
    const resp = await fetch(`${apiBase}/tenant-config?host=${encodeURIComponent(host)}`);
    if (!resp.ok) return false;
    const cfg = await resp.json();
    const next = { url: cfg.authServerUrl, realm: cfg.realm, clientId: cfg.clientId,
                   org: cfg.organizationId, isDefault: cfg.isDefault };
    const prev = window.localStorage.getItem(TENANT_KC_KEY);
    const nextStr = JSON.stringify(next);
    window.localStorage.setItem(TENANT_KC_KEY, nextStr);
    return prev !== nextStr; // changed → caller may reload to re-init Keycloak
  } catch (e) {
    console.warn('Tenant config bootstrap failed; using default Keycloak config:', e);
    return false;
  }
};

// Resolve the Keycloak admin console URL for the active environment.
// Used by the "Open Keycloak Admin" action so a superadmin is forwarded to the
// local console in local/dev and to the designated Keycloak login page in
// dev/qa/prod (and to the per-tenant realm when federation is enabled).
export const getKeycloakAdminUrl = () => {
  const cfg = keycloakConfig || getKeycloakConfig();
  const base = (cfg.url || 'http://localhost:8080').replace(/\/+$/, '');
  return `${base}/admin/`;
};

// Global singleton Keycloak instance to prevent multiple initialization
let keycloakInstance = null;
let keycloakConfig = null;
let isInitialized = false;

// Create a global reference to prevent garbage collection
if (typeof window !== 'undefined') {
  window.__KEYCLOAK_INSTANCE__ = window.__KEYCLOAK_INSTANCE__ || null;
}

const createKeycloakInstance = () => {
  // Check if we already have a global instance
  if (typeof window !== 'undefined' && window.__KEYCLOAK_INSTANCE__) {
    console.log('Using existing global Keycloak instance');
    return window.__KEYCLOAK_INSTANCE__;
  }

  if (!keycloakInstance) {
    keycloakConfig = getKeycloakConfig();
    keycloakInstance = new Keycloak(keycloakConfig);
    
    // Store in global reference
    if (typeof window !== 'undefined') {
      window.__KEYCLOAK_INSTANCE__ = keycloakInstance;
    }
    
    console.log('New Keycloak instance created with config:', keycloakConfig);
  }
  
  return keycloakInstance;
};

// Keycloak initialization options for SPA OIDC Auth Code + PKCE flow
export const keycloakInitOptions = {
  // Don't automatically check SSO or login - wait for user action
  checkLoginIframe: false,
  enableLogging: import.meta.env.DEV,
  // Use Authorization Code flow with PKCE (recommended for SPAs)
  flow: 'standard',
  responseMode: 'fragment',
  pkceMethod: 'S256',
  // Additional security options
  scope: 'openid profile email',
  redirectUri: window.location.origin,
  // Disable features that might cause third-party cookie issues
  checkLoginIframeInterval: 0,
  messageReceiveTimeout: 10000,
  // Prevent automatic initialization checks
  adapter: 'default',
};

// Check if we should bypass authentication in development
const shouldBypassAuth = import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true';

// Export the singleton instance (or null if bypassing)
export const keycloak = shouldBypassAuth ? null : createKeycloakInstance();
export { keycloakConfig };
export default keycloak;
